<template>
    <div class="answer-button__container">
        <make-model-vehicle-image-component :chatMessage="chatMessage" :blurred="notSure" />
        <div class="colour-selector">
            <label v-for="(color, index) in chatMessage.options.choices" :key="index" :for="`colour-${color}`">
                <img v-if="basicColour === color" class="colour-selector__selected"
                    :src="`${imagePath}/colourSelected.svg`" alt="Selected Colour" />
                <img v-if="basicColour !== color" class="colour-selector__selected" :src="`${imagePath}/deselected.svg`"
                    alt="Deselected Colour" />
                <input type="radio" :id="`colour-${color}`" :value="color" class="colour-touchpoint__input"
                    v-model="basicColour" :disabled="disableColours" />
                <span v-html="buildImage(color)"></span>
            </label>
        </div>
    </div>
</template>

<script lang="ts">
import { Validator } from 'vee-validate';
import {
    Component,
    Prop,
    Vue,
    Inject
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import SearchQuery from '@/models/SearchQuery';
import ChatMessage from '@/models/vehicle/makeModelBot/chatMessage';
import { imageUrlService } from '@/services/ImageUrlService';
import Derivative from '@/models/vehicle/derivative';
import { store } from '@/store';
import { vehiclePaintCodeProviderService } from '@/services/VehiclePaintCodeProviderService';
import MakeModelVehicleImageComponent from './MakeModelVehicleImageComponent.vue';

const SearchQueryModule = namespace('searchQueryModule');
const VehicleModule = namespace('vehicleModule');

@Component({
    components: {
        'make-model-vehicle-image-component': MakeModelVehicleImageComponent
    }
})
export default class ColourPickerComponent extends Vue {
    @Inject('$validator') public $validator!: Validator;

    @VehicleModule.Getter public currentDerivative!: Derivative | null;

    @SearchQueryModule.Action public submitBasicColour;

    @SearchQueryModule.Getter public searchQuery!: SearchQuery;

    @Prop({ default: '' })
    public chatMessage!: ChatMessage;

    @Prop({ default: false })
    public disableColours!: boolean;

    private preloadedImages: Record<string, string> = {};

    public notSure: boolean = false;

    public get basicColour() {
        return this.searchQuery.basicColour || '';
    }

    public set basicColour(colour: string) {
        if (colour.toLowerCase() === 'not sure') {
            this.notSure = true;
        } else {
            this.notSure = false;
        }
        this.$emit('answerChange', this.notSure);
        this.submitBasicColour(colour);
        this.$emit('colourSelected', this.formatClassColour(colour || ''));
        Vue.set(this.chatMessage.options, 'imageURL', this.preloadedImages[colour.toLocaleLowerCase().replace(' ', '') || '']);
    }

    public formatClassColour(colour: string) {
        return colour.replace(/\s+|[/]/g, '').toLowerCase();
    }

    public async preloadImages() {
        this.chatMessage.options?.choices?.forEach((colour) => {
            const formattedColor = this.formatClassColour(colour);
            const manufacturerName = store.getters['vehicleModule/currentRange']?.manufacturerName;
            const imageUrl = imageUrlService.imageUrl(undefined, undefined, undefined, undefined, undefined, vehiclePaintCodeProviderService.GetPaintCode(formattedColor, manufacturerName));
            const img = new Image();
            img.src = imageUrl;
            this.preloadedImages[formattedColor] = imageUrl;
        });
    }

    private buildImage(colour: string): string {
        return `<img src="${`${process.env.BASE_URL}images/detailed-colours`}/${colour.toLowerCase().replace(/ /g, '-')}.png" class="colour-selector__image" />`;
    }

    public get imagePath() {
        return `${process.env.BASE_URL}images/detailed-colours`;
    }

    async mounted() {
        this.chatMessage.isFinished = true;
        await this.preloadImages();
        if (this.chatMessage.options.choices) {
            const colour = this.chatMessage.options.choices[0];
            Vue.set(this.chatMessage.options, 'imageURL', this.preloadedImages[colour.toLocaleLowerCase().replace(/ /g, '') || '']);
        }
    }
}
</script>
